import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { onEvent } from '../lib/ws';

// ── State chip ────────────────────────────────────────────────────────────────
const STATE_STYLES: Record<string, string> = {
  'idle':        'badge bg-success/20 text-success',
  'in-call':     'badge bg-warning/20 text-warning',
  'wrap-up':     'badge',
  'unavailable': 'badge',
};
const STATE_LABELS: Record<string, string> = {
  'idle':        'Idle',
  'in-call':     'In Call',
  'wrap-up':     'Wrap-up',
  'unavailable': 'Away',
};

function StateChip({ state }: { state: string }) {
  const cls   = STATE_STYLES[state] ?? 'badge';
  const label = STATE_LABELS[state] ?? state;
  return <span className={cls}>{label}</span>;
}

function fmtDuration(sec: number): string {
  if (!sec || sec <= 0) return '0s';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

// ── Per-agent table ───────────────────────────────────────────────────────────
function AgentTable({ agents }: { agents: any[] }) {
  if (!agents || agents.length === 0) {
    return <p className="text-textSecondary text-xs mt-2">No agents logged in.</p>;
  }
  return (
    <table className="w-full text-sm mt-3">
      <thead>
        <tr className="text-textSecondary text-xs border-b border-border">
          <th className="text-left pb-1 font-normal">Agent</th>
          <th className="text-left pb-1 font-normal">State</th>
          <th className="text-left pb-1 font-normal">Time in state</th>
          <th className="text-right pb-1 font-normal">Calls today</th>
        </tr>
      </thead>
      <tbody>
        {agents.map((a: any, i: number) => (
          <tr key={a.name || i} className="border-b border-border/40 last:border-0">
            <td className="py-1 font-mono">{a.extension || a.name}</td>
            <td className="py-1"><StateChip state={a.state} /></td>
            <td className="py-1 text-textSecondary">{fmtDuration(a.state_seconds)}</td>
            <td className="py-1 text-right font-mono">{a.calls_answered ?? 0}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Queue card ────────────────────────────────────────────────────────────────
function QueueCard({ q }: { q: any }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold">{q.name}</h3>
        <span className="text-xs text-textSecondary">ext {q.extension}</span>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <Mini label="Agents (avail / total)" value={`${q.agents_available ?? 0} / ${q.agents_total ?? 0}`} />
        <Mini label="Callers waiting"        value={q.callers_waiting ?? 0} />
        <Mini label="Avg wait (s)"           value={q.avg_wait_seconds ?? 0} />
        <Mini label="On call"                value={q.agents_on_call ?? 0} />
        <Mini label="Longest wait (s)"       value={q.longest_wait_sec ?? 0} />
        <Mini
          label="Abandon rate"
          value={q.calls_answered == null && q.calls_abandoned == null
            ? '—'
            : `${q.abandon_rate ?? 0}%`}
        />
      </div>
      {(q.agents?.length ?? 0) > 0 && (
        <button
          onClick={() => setExpanded(e => !e)}
          className="btn-ghost text-xs mt-3 w-full text-left"
        >
          {expanded ? 'Hide agents' : `Show ${q.agents.length} agent${q.agents.length !== 1 ? 's' : ''}`}
        </button>
      )}
      {expanded && <AgentTable agents={q.agents ?? []} />}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function Wallboard() {
  const { data: snap, refetch } = useQuery({
    queryKey: ['wallboard'],
    queryFn: () => api.wallboard.snapshot(),
    refetchInterval: 5_000,
  });
  const [live, setLive] = useState<any>(null);

  useEffect(() => {
    const off = onEvent('wallboard.snapshot', (data) => setLive(data));
    return () => off();
  }, []);

  const data = live || snap;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Wallboard</h1>
        <button onClick={() => refetch()} className="btn-ghost text-sm">Refresh</button>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <Stat label="Active Calls"        value={data?.active_calls ?? '—'} />
        <Stat label="Registered Phones"   value={data?.registered_extensions ?? '—'} />
        <Stat label="Avg MOS (1h)"        value={data?.quality?.avg ?? '—'} />
      </div>
      <h2 className="text-lg font-semibold mt-6">Queues</h2>
      <div className="grid grid-cols-2 gap-4">
        {(data?.queues || []).map((q: any) => (
          <QueueCard key={q.queue_uuid} q={q} />
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: any }) {
  return (
    <div className="card text-center">
      <div className="text-textSecondary text-sm">{label}</div>
      <div className="text-4xl font-semibold mt-2">{value}</div>
    </div>
  );
}
function Mini({ label, value }: { label: string; value: any }) {
  return (
    <div>
      <div className="text-textSecondary text-xs">{label}</div>
      <div className="font-mono text-base">{value}</div>
    </div>
  );
}
